import { DateTime, Interval } from "luxon";
import type { Band, CcmsSuspension } from "@/types";

export type ActiveYearsBandRow = {
  id: string;
  program: string;
  classification: string | null;
  section: string | null;
  startedOn: string;
  endedOn: string;
  ongoing: boolean;
  days: number;
};

export type ActiveYearsBreakdown = {
  bandRows: ActiveYearsBandRow[];
  mergedBandDays: number;
  suspensionDays: number;
  estimatedActiveDays: number;
};

const parseDay = (value: string | null | undefined): DateTime | null => {
  if (!value) return null;
  const dt = DateTime.fromISO(value);
  return dt.isValid ? dt.startOf("day") : null;
};

const intervalFrom = (start: DateTime, end: DateTime): Interval | null => {
  if (end <= start) return null;
  return Interval.fromDateTimes(start, end);
};

const mergeIntervals = (intervals: Interval[]): Interval[] => {
  if (intervals.length === 0) return [];
  const sorted = [...intervals].sort((a, b) => a.start!.toMillis() - b.start!.toMillis());
  const merged: Interval[] = [sorted[0]];
  for (let i = 1; i < sorted.length; i++) {
    const last = merged[merged.length - 1];
    const next = sorted[i];
    if (next.start! <= last.end!) {
      merged[merged.length - 1] = Interval.fromDateTimes(last.start!, next.end! > last.end! ? next.end! : last.end!);
    } else {
      merged.push(next);
    }
  }
  return merged;
};

const overlapDays = (a: Interval, b: Interval): number => {
  const overlap = a.intersection(b);
  if (!overlap) return 0;
  return Math.max(0, Math.round(overlap.length("days")));
};

export const formatYearsAndMonths = (days: number): string => {
  if (days <= 0) return "0 mos";
  const now = DateTime.now();
  const diff = now.diff(now.minus({ days }), ["years", "months"], { conversionAccuracy: "longterm" });
  const years = Math.floor(diff.years ?? 0);
  const months = Math.floor(diff.months ?? 0);
  if (years === 0) return `${months} mos`;
  return `${years} yrs ${months} mos`;
};

export const buildActiveYearsBreakdown = (
  bands: Band[] | undefined | null,
  suspensions: CcmsSuspension[] | undefined | null,
  now: DateTime = DateTime.now()
): ActiveYearsBreakdown => {
  const today = now.startOf("day");

  const memberBands = (bands ?? []).filter((b) => b?.programType?.name === "Member");

  const bandRows: ActiveYearsBandRow[] = [];
  const bandIntervals: Interval[] = [];

  for (const band of memberBands) {
    const start = parseDay(band.startedOn);
    if (!start) continue;
    const terminates = parseDay(band.terminatesOn);
    const expires = parseDay(band.expiresOn);
    const candidates = [today, terminates, expires].filter((d): d is DateTime => !!d);
    const end = candidates.reduce((min, d) => (d < min ? d : min), today);
    const interval = intervalFrom(start, end);
    if (!interval) continue;

    bandIntervals.push(interval);
    const ongoing = !terminates && (!expires || expires > today);
    bandRows.push({
      id: band.id,
      program: band.programType?.name || "Member",
      classification: band.classification?.name ?? null,
      section: band.sectionAffiliation?.section?.name || band.section?.name || null,
      startedOn: band.startedOn,
      endedOn: end.toISODate() ?? "",
      ongoing,
      days: Math.max(0, Math.round(interval.length("days")))
    });
  }

  bandRows.sort((a, b) => b.startedOn.localeCompare(a.startedOn));

  const merged = mergeIntervals(bandIntervals);
  const totalBandDays = merged.reduce((sum, i) => sum + Math.max(0, Math.round(i.length("days"))), 0);

  const suspensionIntervals: Interval[] = [];
  for (const s of suspensions ?? []) {
    const sStart = parseDay(s.startDate);
    if (!sStart) continue;
    const sEnd = parseDay(s.endDate) ?? today;
    const sInterval = intervalFrom(sStart, sEnd);
    if (sInterval) suspensionIntervals.push(sInterval);
  }
  const mergedSuspensions = mergeIntervals(suspensionIntervals);
  let suspensionDays = 0;
  for (const s of mergedSuspensions) {
    for (const m of merged) {
      suspensionDays += overlapDays(s, m);
    }
  }

  const estimatedActiveDays = Math.max(0, totalBandDays - suspensionDays);

  return { bandRows, mergedBandDays: totalBandDays, suspensionDays, estimatedActiveDays };
};
